import {
  Image as ChakraImage,
  SimpleGrid,
  Skeleton,
  Stack,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import type { Product, ProductHighlight } from '@diamond/shared/types';
import { Button } from '@diamond/shared/ui';
import { LIST_FEATURE_FLAGS, useFeatureFlag } from '@diamond/shared/utils';
import {
  SCROLL_DEBOUNCE_TIME,
  useGetHomepageHighlights,
  useViewportTracking,
} from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ProductHomepage } from './ProductHomepage';

interface EmptyHighlightProps {
  readonly onFindProduct: () => void;
}

const EmptyHighlight: React.FC<EmptyHighlightProps> = ({ onFindProduct }) => {
  return (
    <VStack paddingTop="8" spacing={4}>
      <ChakraImage
        src="/images/highlight_Illustration.png"
        alt="Empty Highlight"
        objectFit="cover"
        display="block"
        marginX="auto"
        height={{ base: '200px', md: '300px' }}
        width={{ base: '150px', md: '200px' }}
      />
      <Button
        variant="solid"
        bg="black"
        color="white"
        _hover={{ bg: 'gray.800' }}
        width={{ base: '80%', md: '30%' }}
        aria-label="Cari Produk"
        onClick={onFindProduct}
      >
        Cari Produk
      </Button>
    </VStack>
  );
};

const ProductHighlightSkeletonGrid: React.FC = () => {
  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 4, md: 8 }}>
      <Skeleton height="400px" borderRadius="md" />
      <Skeleton height="400px" borderRadius="md" />
      <Skeleton
        display={{ base: 'none', md: 'block' }}
        height="400px"
        borderRadius="md"
      />
      <Skeleton
        display={{ base: 'none', md: 'block' }}
        height="400px"
        borderRadius="md"
      />
    </SimpleGrid>
  );
};

const DESKTOP_VISIBLE_PRODUCTS = 6;
const MOBILE_MIN_VISIBLE_PRODUCTS = 1;
const MOBILE_CARD_WIDTH = 236;

interface TrackedProductHighlightProps {
  highlight: ProductHighlight[0];
  index: number;
}

const TrackedProductHighlight: React.FC<TrackedProductHighlightProps> = ({
  highlight,
  index,
}) => {
  const products = useMemo(
    () => highlight?.products ?? [],
    [highlight?.products]
  );
  const [isMobile] = useMediaQuery('(max-width: 992px)');
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  );
  const [currentStartIndex, setCurrentStartIndex] = useState(0);

  const trackingConfig = useMemo(
    () => ({
      sectionName: `Product Highlight | ${highlight?.title ?? 'Unknown'}`,
      pageName: 'Beranda', // TRACKING_PAGES.BERANDA value
      listId: `Product Highlight | ${highlight?.id ?? 'unknown'}`,
      listName: `Product Highlight | ${highlight?.title ?? 'Unknown'}`,
    }),
    [highlight?.id, highlight?.title]
  );

  const {
    sectionRef,
    trackPaginationChange,
    setupMobileScrollTracking,
    setupVerticalScrollTracking,
    resetScrollFlag,
    isSectionInViewport,
  } = useViewportTracking(products, trackingConfig, {
    intersectionThreshold: 0.65,
    trackIndividualItems: true,
    trackPromotionItems: true, // Enable individual promotion tracking per product
    scrollDebounceTime: SCROLL_DEBOUNCE_TIME,
  });

  const handlePageChange = useCallback(
    (startIndex: number) => {
      setCurrentStartIndex(startIndex);

      // Track pagination changes for both desktop and mobile
      // This ensures ALL pages (including page 1 at index 0) are tracked when navigated to
      if (!isMobile && isSectionInViewport && products.length > 0) {
        const visibleProducts = products.slice(
          startIndex,
          startIndex + DESKTOP_VISIBLE_PRODUCTS
        );

        if (visibleProducts.length > 0) {
          // useViewportTracking handles both VIEW_ITEM_LIST and individual VIEW_PROMOTION events
          // Re-tracking enabled: will fire again when navigating back to previously viewed pages
          trackPaginationChange(visibleProducts, startIndex);
        }
      }
    },
    [isMobile, isSectionInViewport, products, trackPaginationChange]
  );

  const handleScrollContainerReady = useCallback(
    (container: HTMLDivElement | null) => {
      setScrollContainer(container);
    },
    []
  );

  // Mobile horizontal scroll tracking for product carousels within a section
  useEffect(() => {
    if (!isMobile || !scrollContainer) return;

    const cleanup = setupMobileScrollTracking(
      scrollContainer,
      (scrollLeft, clientWidth) => {
        // Enhanced visibility calculation with 65% threshold for mobile horizontal scroll
        const visibleProducts: Product[] = [];
        let startIndex = -1;

        // Check each product for 65% horizontal visibility
        products.forEach((product, index) => {
          const cardLeft = index * MOBILE_CARD_WIDTH;
          const cardRight = cardLeft + MOBILE_CARD_WIDTH;
          const scrollRight = scrollLeft + clientWidth;

          // Calculate visible width of the card
          const visibleLeft = Math.max(cardLeft, scrollLeft);
          const visibleRight = Math.min(cardRight, scrollRight);
          const visibleWidth = Math.max(0, visibleRight - visibleLeft);
          const visibilityPercentage = visibleWidth / MOBILE_CARD_WIDTH;

          if (visibilityPercentage >= 0.65) {
            if (startIndex === -1) {
              startIndex = index;
            }
            visibleProducts.push(product);
          }
        });

        return {
          products: visibleProducts,
          startIndex: startIndex >= 0 ? startIndex : 0,
        };
      }
    );

    return cleanup;
  }, [isMobile, products, scrollContainer, setupMobileScrollTracking]);

  // Mobile vertical scroll tracking using centralized setupVerticalScrollTracking
  useEffect(() => {
    if (!isMobile || products.length === 0 || !sectionRef.current) return;

    const cleanup = setupVerticalScrollTracking(
      sectionRef.current,
      (sectionRect) => {
        // Calculate visible products for mobile vertical scroll
        const visibleCount = Math.min(
          MOBILE_MIN_VISIBLE_PRODUCTS,
          products.length
        );
        const visibleProducts = products.slice(0, visibleCount);

        return {
          products: visibleProducts,
          startIndex: 0,
        };
      },
      {
        visibilityThreshold: 0.65,
        debounceTime: 100, // Fast response for mobile, only fires when scroll stops
        enableReTracking: true, // Enable re-tracking when scrolling back
      }
    );

    return cleanup;
  }, [
    isMobile,
    products,
    sectionRef,
    setupVerticalScrollTracking,
    highlight?.title,
  ]);

  useEffect(() => {
    setCurrentStartIndex(0);
    resetScrollFlag();
  }, [isMobile, highlight?.id, resetScrollFlag]);

  if (products.length === 0) return null;

  return (
    <ProductHomepage
      data={highlight}
      index={index}
      sectionRef={sectionRef}
      onPageChange={handlePageChange}
      onScrollContainerReady={handleScrollContainerReady}
    />
  );
};

export const HighlightsSection = () => {
  const { push } = useRouter();
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch by ensuring consistent initial render
  useEffect(() => {
    setMounted(true);
  }, []);

  const isNewCg3 = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_HIGHLIGHT_CG3_NEW,
    false
  );
  const {
    data: highlights,
    isPending,
    isFetching,
    error,
  } = useGetHomepageHighlights({
    enabled: isNewCg3,
  });

  const handleCariProduk = () => {
    push({
      pathname: `/product`,
      query: { cat1: '' },
    });
  };

  const isLoading = Boolean(isPending || isFetching);
  const hasNoData = !isLoading && !error && (highlights?.length ?? 0) === 0;
  const hasData = !isLoading && !error && (highlights?.length ?? 0) > 0;

  // During SSR and initial hydration, always show loading to prevent hydration mismatch
  if (!mounted || isLoading) {
    return (
      <Stack direction="column" spacing={{ base: 2, md: 20 }}>
        <ProductHighlightSkeletonGrid />
      </Stack>
    );
  }

  if (hasNoData) {
    return (
      <Stack direction="column" spacing={{ base: 2, md: 20 }}>
        <EmptyHighlight onFindProduct={handleCariProduk} />
      </Stack>
    );
  }

  if (hasData && Array.isArray(highlights)) {
    return (
      <Stack direction="column" spacing={{ base: 2, md: 20 }}>
        {highlights.map((val, idx) => (
          <TrackedProductHighlight
            key={val?.id ?? idx}
            highlight={val}
            index={idx}
          />
        ))}
      </Stack>
    );
  }

  return null;
};

export default HighlightsSection;
